import * as React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

// The delayed first-paint gate: invisible for the first ~200ms, then fades
// in. Pure CSS (`@starting-style` + a delayed opacity transition) — no
// timers, and siblings mounted together fade as one. Shared by `Skeleton
// delayed` and `SkeletonGroup` so the bar and the chrome around it cannot
// drift apart.
export const DELAYED_FADE =
  "transition-opacity delay-200 duration-150 starting:opacity-0";

const FADE_DELAY_MS = 200;
// A gate that mounts this soon after a visible one left takes over at once:
// a pane skeleton handing off to the content's own skeleton would otherwise
// blank for a second delay between the two.
const HANDOFF_MS = 250;
const mountedGates = new Set<{ since: number }>();
let lastVisibleEnd = Number.NEGATIVE_INFINITY;

/** Forgets every indicator seen, so a test starts from a cold screen. */
export function resetLoadingHandoff(): void {
  mountedGates.clear();
  lastVisibleEnd = Number.NEGATIVE_INFINITY;
}

function skeletonOnScreen(now: number): boolean {
  if (now - lastVisibleEnd < HANDOFF_MS) return true;
  for (const gate of mountedGates) if (now - gate.since >= FADE_DELAY_MS) return true;
  return false;
}

/**
 * Registers a loading indicator and returns its delayed-fade class, or none
 * when an indicator is already on screen (or just left it): the loading state
 * is then continuing, not starting, and must not blink out for another delay.
 * Undelayed indicators register too, so a spinner handing off to a skeleton
 * counts as on screen.
 */
export function useDelayedFade(delayed: boolean): string | false {
  const [immediate] = React.useState(
    () => delayed && typeof performance !== "undefined" && skeletonOnScreen(performance.now()),
  );
  const gated = delayed && !immediate;
  React.useEffect(() => {
    const now = performance.now();
    const gate = { since: gated ? now : now - FADE_DELAY_MS };
    mountedGates.add(gate);
    return () => {
      mountedGates.delete(gate);
      const end = performance.now();
      if (end - gate.since >= FADE_DELAY_MS) lastVisibleEnd = end;
    };
  }, [gated]);
  return gated && DELAYED_FADE;
}

export type SkeletonProps = React.ComponentProps<"div"> &
  Omittable<{
  /** Bar width. A number is pixels; a string is any CSS length. */
  width?: number | string;
  /** Bar height. A number is pixels; a string is any CSS length. */
  height?: number | string;
  /** Fills the inline axis, for a bar that spans its container. */
  fillWidth?: boolean;
  /** Circular, for an avatar or icon placeholder. Pair with `width`. */
  circle?: boolean;
  /**
   * The bar's index in a run. `width` is then the run's base: each bar is
   * scaled by a fixed ratio ramp so consecutive bars come out uneven, like
   * names and sentences do, and a run never reads as a ruled form. Any
   * length works — pixels or a percentage.
   */
  vary?: number;
  /**
   * Invisible for its first ~200ms, then fades in. For first-paint loading
   * gates: data that lands inside the window swaps in with no skeleton ever
   * shown, so a warm cache or a fast query never flashes a placeholder.
   * Pure CSS (`@starting-style` + a delayed opacity transition) — no timers,
   * and siblings mounted together fade as one. Leave off for placeholders
   * that must be present immediately (virtual-scroll row slots).
   */
  delayed?: boolean;
}>;

/** The ratio ramp `vary` cycles a run of bars through. */
const VARY_RAMP = [1, 0.7, 1.15, 0.85, 0.6, 0.9] as const;

/** `length` scaled by the ramp step for bar `index`; a percentage is capped
 *  at 100. */
export function variedLength(
  length: number | string,
  index: number,
): number | string {
  const ratio = VARY_RAMP[Math.abs(index) % VARY_RAMP.length] ?? 1;
  if (typeof length === "number") return Math.round(length * ratio);
  const match = /^(-?[\d.]+)(.*)$/.exec(length.trim());
  if (!match) return length;
  const scaled = Number(match[1]) * ratio;
  const unit = match[2] ?? "";
  return `${unit === "%" ? Math.min(100, Math.round(scaled)) : Math.round(scaled * 100) / 100}${unit}`;
}

export function Skeleton({
  className,
  width: baseWidth,
  height,
  fillWidth = false,
  circle = false,
  vary,
  delayed = false,
  style,
  ...props
}: SkeletonProps): React.ReactElement {
  const fade = useDelayedFade(delayed);
  const width =
    vary != null && baseWidth != null ? variedLength(baseWidth, vary) : baseWidth;
  // A skeleton is defined by its dimensions, so they are the props. Every other
  // primitive here takes sizing the same way (see `Box width`/`height`), and a
  // placeholder is the one component where a bare className is almost always
  // just a width.
  const sizing: React.CSSProperties = {
    ...(width != null && { width }),
    ...(height != null && { height }),
    ...(circle && width != null && { height: width }),
  };

  // The bar is a WASH — a translucent foreground tint — with a moving
  // highlight over it, never an opaque grey. A wash composites against
  // whatever it lies on, so it reads on the page, on a card and in a popover
  // alike, in both themes. An opaque token cannot promise that: the dark
  // theme's surfaces climb from the page to the popover, and a grey chosen
  // for the page sits BELOW a card — a skeleton row on a dark card was then
  // indistinguishable from an empty one. The raw token, not its `--color-*`
  // theme alias: the alias is emitted only where a utility class uses it,
  // and this arbitrary `background` is the fill's only reference.
  return (
    <div
      className={cn(
        "animate-skeleton rounded-sm [--skeleton-highlight:--alpha(var(--color-white)/64%)] [background:linear-gradient(120deg,transparent_40%,var(--skeleton-highlight),transparent_60%)_var(--wash-skeleton)_0_0/200%_100%_fixed] dark:[--skeleton-highlight:--alpha(var(--color-white)/4%)]",
        fillWidth && "w-full",
        circle && "rounded-full",
        fade,
        className,
      )}
      data-slot="skeleton"
      style={Object.keys(sizing).length ? { ...sizing, ...style } : style}
      {...props}
    />
  );
}

export type SkeletonGroupProps = React.ComponentProps<"div">;

/**
 * Gates a whole loading scaffold — skeleton bars plus the chrome that frames
 * them (labels, icons, separators) — behind the same delayed first paint a
 * lone `Skeleton delayed` applies to its bar. Without it the chrome paints on
 * the first frame while only the bars wait, so an answer that lands inside
 * the window still flashes a block of labeled empty rows. Wrap the block in
 * this and nothing paints until the delay elapses; fast data swaps in with no
 * placeholder ever shown.
 */
export function SkeletonGroup({
  className,
  ...props
}: SkeletonGroupProps): React.ReactElement {
  const fade = useDelayedFade(true);
  return (
    <div
      className={cn(fade, className)}
      data-slot="skeleton-group"
      {...props}
    />
  );
}
